import React from 'react';
import { injectIntl } from "react-intl";
import { Link } from 'react-router-dom';
import { isIE, isSafari } from 'react-device-detect';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import LinearProgress from '@material-ui/core/LinearProgress';
import InputAdornment from '@material-ui/core/InputAdornment';

import CustomHelmet from "@app/components/CustomHelmets/CustomHelmet";
import CustomHeader from '@app/components/Header/CustomHeader';
import GridContainer from '@app/components/Grid/GridContainer';
import GridItem from '@app/components/Grid/GridItem';
import Button from '@app/components/CustomButtons/Button';
import Card from '@app/components/Card/Card';
import CardBody from '@app/components/Card/CardBody';
import CardHeader from '@app/components/Card/CardHeader';
import CardFooter from '@app/components/Card/CardFooter';
import CustomInput from '@app/components/CustomInput/CustomInput';
import Message from '@app/components/Message/Message';

import Email from '@material-ui/icons/Email';
import Lock from '@material-ui/icons/Lock';

import * as paths from '@app/constants/paths';

import signInPageStyle from '@app/assets/jss/material-kit-pro-react/views/signInPageStyle';

import bgImgWEBP from '@app/assets/img/bg/professionals-1440w.webp';
import bgImgJP2 from '@app/assets/img/bg/professionals-1440w.jp2';

const SignInView = (props) => {
    const { classes, isPasswordSignIn } = props;

    const isWebpSupported = !(isIE || isSafari);

    const appName = props.intl.formatMessage({ id: "app.name" });
    
    const emailSuccess = props.isEmailValid;
    const emailError = props.validationErrors.email.show && !props.isEmailValid;
    const emailHelperText = props.validationErrors.email.show ? props.validationErrors.email.errorMessage : '';
    const passwordSuccess = props.isPasswordValid;
    const passwordError = props.validationErrors.password.show && !props.isPasswordValid;
    const passwordHelperText = props.validationErrors.password.show ? props.validationErrors.password.errorMessage : '';

    const helmetTitle = props.intl.formatMessage({ id: "helmet.title" });
    const metaDescription = props.intl.formatMessage({ id: "meta.description" });
    const msTileColor = props.intl.formatMessage({ id: "meta.msTileColor" });
    const themeColor = props.intl.formatMessage({ id: "meta.themeColor" });
    const twitterCardType = props.intl.formatMessage({ id: "meta.twitterCardType" });
    const twitterSite = props.intl.formatMessage({ id: "meta.twitterSite" });
    const twitterCreator = props.intl.formatMessage({ id: "meta.twitterCreator" });
    const twitterDescription = props.intl.formatMessage({ id: "meta.twitterDescription" });
    const twitterTitle = props.intl.formatMessage({ id: "meta.twitterTitle" });
    const twitterImage = props.intl.formatMessage({ id: "meta.twitterImage" });
    const twitterImageAlt = props.intl.formatMessage({ id: "meta.twitterImageAlt" });
    const ogType = props.intl.formatMessage({ id: "og.type" });
    const ogSiteName = props.intl.formatMessage({ id: "og.site.name" });
    const ogTitle = props.intl.formatMessage({ id: "og.title" });
    const ogDescription = props.intl.formatMessage({ id: "og.description" });
    const ogImage = props.intl.formatMessage({ id: "og.image" });
    
    const signIn = props.intl.formatMessage({ id: "log.in" });
    const email = props.intl.formatMessage({ id: "email" });
    const pass = props.intl.formatMessage({ id: "password" });
    const noAccount = props.intl.formatMessage({ id: "no.account" });
    const signUp = props.intl.formatMessage({ id: "sign.up" });
    const getStarted = props.intl.formatMessage({ id: "get.started" });

    return (
        <div>
            <CustomHelmet
                helmetTitle={helmetTitle}
                metaDescription={metaDescription}
                msTileColor={msTileColor}
                themeColor={themeColor}
                twitterCardType={twitterCardType}
                twitterSite={twitterSite}
                twitterCreator={twitterCreator}
                twitterDescription={twitterDescription}
                twitterTitle={twitterTitle}
                twitterImage={twitterImage}
                twitterImageAlt={twitterImageAlt}
                ogType={ogType}
                ogSiteName={ogSiteName}
                ogTitle={ogTitle}
                ogDescription={ogDescription}
                ogImage={ogImage}
            />
            <CustomHeader
                brandName={appName}
            />
            <div className={classes.main} >                
                <div className={classes.backgroundContainer} style={{
                        backgroundImage: `url(${isWebpSupported ? bgImgWEBP : bgImgJP2})`
                    }}>           
                    <div className={classes.progress}>
                        {props.isLoading &&
                            <LinearProgress />
                        }
                    </div>
                    {(props.errorMessage || props.infoMessage) &&
                        <GridContainer justify='center'>
                            <GridItem xs={12} sm={8} md={5} lg={4}>
                                {props.errorMessage &&
                                    <Message type="error" message={props.errorMessage} />
                                }
                                {props.infoMessage &&
                                    <Message type="info" message={props.infoMessage} />
                                }
                            </GridItem>
                        </GridContainer>
                    }
                    <GridContainer justify='center'>
                        <GridItem xs={12} sm={8} md={5} lg={4}>
                            <Card className={classes.cardSignIn}>
                                <CardHeader color='danger' className={classes.cardHeaderSignIn}>
                                    <h4 className={classes.cardTitle}>
                                        {signIn}
                                    </h4>
                                    <div className={classes.socialLine}>
                                        <Button justIcon
                                            color='transparent'
                                            className={classes.iconButtons}
                                            onClick={props.onClickSignInWithGoogle}>
                                            <i className='fab fa-google' />
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardBody className={classes.cardBodySignIn}>
                                    <form className={classes.form} name='form' onSubmit={props.onSubmit}>
                                        <CustomInput
                                            id='email'
                                            helperText={emailHelperText}
                                            success={emailSuccess}
                                            error={emailError}
                                            formControlProps={{
                                                fullWidth: true
                                            }}
                                            inputProps={{
                                                placeholder: email,
                                                name: 'email',
                                                value: props.email,
                                                onChange: props.onEmailChange,
                                                autoComplete: 'email',
                                                startAdornment: (
                                                    <InputAdornment position='start'>
                                                        <Email className={classes.inputIconsColor} />
                                                    </InputAdornment>
                                                )
                                            }} 
                                        />
                                        {isPasswordSignIn &&
                                            <CustomInput
                                                id='password'
                                                helperText={passwordHelperText}
                                                success={passwordSuccess}
                                                error={passwordError}
                                                formControlProps={{
                                                    fullWidth: true
                                                }}
                                                inputProps={{
                                                    placeholder: pass,
                                                    name: 'password',
                                                    type: 'password',
                                                    value: props.password,
                                                    onChange: props.onPasswordChange,
                                                    autoComplete: 'current-password',
                                                    startAdornment: (
                                                        <InputAdornment position='start'>
                                                            <Lock className={classes.inputIconsColor} />
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        }
                                        <div className={classes.textCenter}>
                                            <Button type='submit' color='danger' size='lg' disabled={props.isSignInDisabled}>
                                                {getStarted}
                                            </Button>
                                        </div>
                                    </form>
                                </CardBody>
                                <CardFooter className={classes.cardFooterSignIn}>
                                    <div>
                                        {noAccount}
                                        {' '}
                                        <Link to={paths.SIGN_UP}>{signUp}</Link>.
                                    </div>
                                </CardFooter>
                            </Card>
                        </GridItem>
                    </GridContainer>
                </div>
            </div>
        </div>
    );
}

SignInView.propTypes = {
    isPasswordSignIn: PropTypes.bool.isRequired,
    email: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isSignInDisabled: PropTypes.bool.isRequired,
    validationErrors: PropTypes.object.isRequired,
    infoMessage: PropTypes.string.isRequired,
    errorMessage: PropTypes.string.isRequired,
    onEmailChange: PropTypes.func.isRequired,
    onPasswordChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClickSignInWithGoogle: PropTypes.func.isRequired,
    onClickSingInWithFacebook: PropTypes.func.isRequired,
    onClickSingInWithTwitter: PropTypes.func.isRequired
};

export default injectIntl(withStyles(signInPageStyle)(SignInView));
